import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { UpgradePromptContext } from '@/types/subscription';

/**
 * Subscription UI state for managing paywall modals and upgrade prompts.
 */
interface SubscriptionState {
  /** Whether the upgrade modal is open */
  isUpgradeModalOpen: boolean;
  /** Context for the upgrade prompt (determines copy shown) */
  upgradePromptContext: UpgradePromptContext | null;
  /** Whether checkout is in progress */
  isCheckoutLoading: boolean;
  /** Error message from checkout */
  checkoutError: string | null;
  /** Coupon code entered by user */
  couponCode: string | null;
  /**
   * D51f Phase 5 — set (by `externalPurchase.ts`) the moment a hosted Stripe
   * page (checkout OR the customer portal) is opened, cleared by the native
   * foreground listener (`useCheckoutForegroundRefresh`) once it has run the
   * post-checkout refresh for that trip. It is what tells an ordinary app
   * foreground apart from "the member just came back from Stripe", so the
   * entitlement/issue caches are refreshed once per trip rather than on every
   * foreground. Never persisted: a killed app starts clean and its first
   * queries are fresh anyway.
   */
  checkoutAttempted: boolean;
}

const initialState: SubscriptionState = {
  isUpgradeModalOpen: false,
  upgradePromptContext: null,
  isCheckoutLoading: false,
  checkoutError: null,
  couponCode: null,
  checkoutAttempted: false,
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    /**
     * Open the upgrade modal with a specific context.
     */
    openUpgradeModal: (state, action: PayloadAction<UpgradePromptContext>) => {
      state.isUpgradeModalOpen = true;
      state.upgradePromptContext = action.payload;
      state.checkoutError = null;
    },
    /**
     * Close the upgrade modal.
     */
    closeUpgradeModal: (state) => {
      state.isUpgradeModalOpen = false;
      state.upgradePromptContext = null;
      state.checkoutError = null;
    },
    /**
     * Set checkout loading state.
     */
    setCheckoutLoading: (state, action: PayloadAction<boolean>) => {
      state.isCheckoutLoading = action.payload;
    },
    /**
     * Set checkout error.
     */
    setCheckoutError: (state, action: PayloadAction<string | null>) => {
      state.checkoutError = action.payload;
    },
    /**
     * Set coupon code.
     */
    setCouponCode: (state, action: PayloadAction<string | null>) => {
      state.couponCode = action.payload;
    },
    /**
     * Clear coupon code.
     */
    clearCouponCode: (state) => {
      state.couponCode = null;
    },
    /** A hosted Stripe page was just opened (D51f Phase 5). */
    markCheckoutAttempted: (state) => {
      state.checkoutAttempted = true;
    },
    /** The post-checkout refresh for that trip has been started (D51f Phase 5). */
    clearCheckoutAttempted: (state) => {
      state.checkoutAttempted = false;
    },
  },
});

export const {
  openUpgradeModal,
  closeUpgradeModal,
  setCheckoutLoading,
  setCheckoutError,
  setCouponCode,
  clearCouponCode,
  markCheckoutAttempted,
  clearCheckoutAttempted,
} = subscriptionSlice.actions;

// Selectors
export const selectIsUpgradeModalOpen = (state: { subscription: SubscriptionState }) =>
  state.subscription.isUpgradeModalOpen;
export const selectUpgradePromptContext = (state: { subscription: SubscriptionState }) =>
  state.subscription.upgradePromptContext;
export const selectIsCheckoutLoading = (state: { subscription: SubscriptionState }) =>
  state.subscription.isCheckoutLoading;
export const selectCheckoutError = (state: { subscription: SubscriptionState }) =>
  state.subscription.checkoutError;
export const selectCouponCode = (state: { subscription: SubscriptionState }) =>
  state.subscription.couponCode;
export const selectCheckoutAttempted = (state: { subscription: SubscriptionState }) =>
  state.subscription.checkoutAttempted;

export const subscriptionReducer = subscriptionSlice.reducer;
